// Trust Admin — Payments dashboard.
// Lists every vendor payment order (CCAvenue-backed) with filters for
// status / type / vendor / date range, CSV export of the filtered rows and
// per-row PDF receipt download / resend actions.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Wallet, Download, Loader2, FileDown, Mail, RefreshCw, Search, Filter,
  CheckCircle2, XCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUSES = [
  { value: "ALL", label: "All statuses" },
  { value: "SUCCESS", label: "Success" },
  { value: "PENDING_VERIFICATION", label: "Pending Verification" },
  { value: "INITIATED", label: "Initiated" },
  { value: "FAILED", label: "Failed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "AMOUNT_MISMATCH", label: "Amount mismatch" },
  { value: "CURRENCY_MISMATCH", label: "Currency mismatch" },
];

const TYPES = [
  { value: "", label: "All types" },
  { value: "vendor_registration", label: "Vendor Registration" },
];

const GATEWAYS = [
  { value: "", label: "All methods" },
  { value: "ccavenue", label: "Online (CCAvenue)" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

const STATUS_STYLES = {
  SUCCESS: "bg-emerald-50 text-emerald-800 border-emerald-200",
  PENDING_VERIFICATION: "bg-amber-50 text-amber-800 border-amber-200",
  INITIATED: "bg-amber-50 text-amber-800 border-amber-200",
  FAILED: "bg-red-50 text-red-800 border-red-200",
  REJECTED: "bg-red-50 text-red-800 border-red-200",
  CANCELLED: "bg-neutral-100 text-neutral-700 border-neutral-200",
  AMOUNT_MISMATCH: "bg-red-50 text-red-800 border-red-200",
  CURRENCY_MISMATCH: "bg-red-50 text-red-800 border-red-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtAmount(v, ccy = "INR") {
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: ccy || "INR", maximumFractionDigits: 2,
  }).format(n);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Payments() {
  const { user } = useAuth();
  const canManage = ["super_admin", "editor", "infrastructure"].includes(user?.role);
  const [status, setStatus] = useState("ALL");
  const [type, setType] = useState("");
  const [gateway, setGateway] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [busyOrder, setBusyOrder] = useState("");
  const [rejectFor, setRejectFor] = useState(null); // { order_id, vendor_firm }
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== "ALL") params.status = status;
      if (type) params.type = type;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/payments", { params });
      setItems(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load payments");
    } finally { setLoading(false); }
  }, [status, type, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Client-side free-text search + gateway filter over the already-fetched
  // (server-filtered) rows so the CSV export stays consistent with what
  // admin sees on screen.
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = items;
    if (gateway) rows = rows.filter((r) => r.gateway === gateway);
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [
        r.order_id, r.status, r.type, r.vendor_firm, r.vendor_email,
        r.vendor_mobile, r.tracking_id, r.bank_ref_no, r.payment_mode, r.utr,
      ].map((v) => String(v || "").toLowerCase()).join(" ");
      return hay.includes(needle);
    });
  }, [items, q]);

  const [pendingCount, setPendingCount] = useState(0);
  const totalCollected = useMemo(() => visible
    .filter((r) => r.status === "SUCCESS")
    .reduce((sum, r) => sum + Number(r.amount || 0), 0), [visible]);

  // Global "pending verification" count — always fetched, independent of filters.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/payments/offline", { params: { status: "PENDING_VERIFICATION" } });
        setPendingCount(data?.total || 0);
      } catch { /* silent */ }
    })();
  }, [items]);

  const resetFilters = () => {
    setStatus("ALL"); setType(""); setGateway(""); setDateFrom(""); setDateTo(""); setQ("");
  };

  const verifyOffline = async (order) => {
    if (!window.confirm(`Verify payment from ${order.vendor_firm || "vendor"} — UTR ${order.utr || order.tracking_id}? This marks the registration as paid and emails the receipt.`)) return;
    setBusyOrder(order.order_id);
    try {
      await api.post(`/admin/payments/offline/${order.order_id}/verify`);
      toast.success("Payment verified. Receipt emailed to vendor.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusyOrder(""); }
  };

  const rejectOffline = async () => {
    if (!rejectReason.trim()) return toast.error("Please provide a rejection reason");
    const order_id = rejectFor.order_id;
    setBusyOrder(order_id);
    try {
      await api.post(`/admin/payments/offline/${order_id}/reject`, { reason: rejectReason.trim() });
      toast.success("Payment rejected. Vendor can resubmit.");
      setRejectFor(null); setRejectReason("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rejection failed");
    } finally { setBusyOrder(""); }
  };

  const exportCsv = async () => {
    // Server-side export mirrors the current server filters. If the user has
    // also typed a search term, we filter client-side and hand-roll a CSV
    // from the visible rows so what they see is what they get.
    try {
      if (q.trim()) {
        const headers = [
          "Order ID", "Type", "Status", "Amount", "Currency",
          "Vendor Firm", "Vendor Email", "Vendor Mobile",
          "Payment Mode", "Tracking ID", "Bank Ref.", "Gateway",
          "Created At", "Completed At",
        ];
        const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
        const rows = visible.map((r) => [
          r.order_id, r.type, r.status, r.amount, r.currency,
          r.vendor_firm, r.vendor_email, r.vendor_mobile,
          r.payment_mode, r.tracking_id, r.bank_ref_no, r.gateway,
          (r.created_at || "").slice(0, 19).replace("T", " "),
          (r.completed_at || "").slice(0, 19).replace("T", " "),
        ].map(esc).join(","));
        const csv = [headers.map(esc).join(","), ...rows].join("\n");
        triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8" }),
          `payments-filtered-${new Date().toISOString().slice(0, 10)}.csv`);
        return;
      }
      const params = new URLSearchParams();
      if (status && status !== "ALL") params.set("status", status);
      if (type) params.set("type", type);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const token = localStorage.getItem("gsm_token");
      const res = await fetch(`${API}/admin/payments/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      triggerDownload(blob, `payments-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      toast.error(err?.message || "Export failed");
    }
  };

  const downloadReceipt = async (orderId) => {
    setBusyOrder(orderId);
    try {
      const token = localStorage.getItem("gsm_token");
      const res = await fetch(`${API}/admin/payments/${orderId}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = "Could not download receipt";
        try { detail = (await res.json())?.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      triggerDownload(blob, `Receipt-${orderId}.pdf`);
    } catch (err) {
      toast.error(err?.message || "Download failed");
    } finally { setBusyOrder(""); }
  };

  const resendReceipt = async (orderId) => {
    setBusyOrder(orderId);
    try {
      const { data } = await api.post(`/admin/payments/${orderId}/resend-receipt`);
      if (data?.success) toast.success("Receipt emailed to vendor");
      else toast.error("Email dispatch did not confirm — check SendGrid logs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally { setBusyOrder(""); }
  };

  return (
    <div className="space-y-5" data-testid="adm-payments">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
            <h1 className="font-serif text-2xl text-[#1c1a17] flex items-center gap-2">
              <Wallet size={18} /> Payments
            </h1>
            <div className="text-sm text-[#6b655b] mt-0.5">
              All vendor payments (CCAvenue online + bank-transfer). Filter,
              export, verify pending transfers, and re-send receipts.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={() => { setStatus("PENDING_VERIFICATION"); setGateway("bank_transfer"); }}
                data-testid="adm-payments-pending-pill"
                className="inline-flex items-center gap-1.5 bg-amber-100 border border-amber-400 hover:bg-amber-200 text-amber-900 text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm"
                title="Show only pending bank-transfer submissions"
              >
                {pendingCount} pending
              </button>
            )}
            <button
              type="button"
              onClick={load}
              data-testid="adm-payments-refresh"
              className="inline-flex items-center gap-1.5 bg-white border border-[#e5e0d8] hover:border-[#234333] text-[#234333] text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!visible.length}
              data-testid="adm-payments-export"
              className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3125] disabled:opacity-40 text-white text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold flex items-center gap-1"><Filter size={10} /> Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="adm-payments-filter-status"
              className="mt-1 w-full h-9 border border-[#e5e0d8] rounded-sm px-2 text-xs bg-white"
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="adm-payments-filter-type"
              className="mt-1 w-full h-9 border border-[#e5e0d8] rounded-sm px-2 text-xs bg-white"
            >
              {TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold">Method</label>
            <select
              value={gateway}
              onChange={(e) => setGateway(e.target.value)}
              data-testid="adm-payments-filter-gateway"
              className="mt-1 w-full h-9 border border-[#e5e0d8] rounded-sm px-2 text-xs bg-white"
            >
              {GATEWAYS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="adm-payments-filter-from"
              className="mt-1 w-full h-9 border border-[#e5e0d8] rounded-sm px-2 text-xs bg-white"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="adm-payments-filter-to"
              className="mt-1 w-full h-9 border border-[#e5e0d8] rounded-sm px-2 text-xs bg-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold flex items-center gap-1"><Search size={10} /> Search</label>
            <div className="mt-1 relative">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Order ID, vendor firm, email, tracking ID…"
                data-testid="adm-payments-search"
                className="w-full h-9 border border-[#e5e0d8] rounded-sm pl-3 pr-16 text-xs bg-white"
              />
              {(status !== "ALL" || type || dateFrom || dateTo || q) && (
                <button
                  type="button"
                  onClick={resetFilters}
                  data-testid="adm-payments-reset"
                  className="absolute right-1 top-1 h-7 px-2 text-[10px] uppercase font-bold text-[#234333] hover:bg-[#f5f1e8] rounded-sm"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryTile label="Rows shown" value={visible.length} testid="adm-payments-count-total" />
          <SummaryTile
            label="Successful"
            value={visible.filter((r) => r.status === "SUCCESS").length}
            accent="text-emerald-700"
            testid="adm-payments-count-success"
          />
          <SummaryTile
            label="Total Collected"
            value={fmtAmount(totalCollected)}
            accent="text-[#234333]"
            testid="adm-payments-total-collected"
          />
        </div>

        {/* Table */}
        <div className="mt-5">
          {loading ? (
            <div className="text-sm text-[#6b655b] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : visible.length === 0 ? (
            <div className="bg-[#f5f1e8] rounded-sm p-8 text-center text-sm text-[#6b655b]" data-testid="adm-payments-empty">
              No payments match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6b655b] border-b border-[#e5e0d8]">
                    <th className="py-2 pr-3">Order ID</th>
                    <th className="py-2 pr-3">Vendor</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Method</th>
                    <th className="py-2 pr-3 text-right">Amount</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Tracking / UTR</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr
                      key={r.order_id}
                      className="border-b border-[#f0ebe0] hover:bg-[#fbf8f1]"
                      data-testid={`adm-payment-row-${r.order_id}`}
                    >
                      <td className="py-2 pr-3 font-mono text-[11px] text-[#1c1a17]">{r.order_id}</td>
                      <td className="py-2 pr-3">
                        <div className="font-semibold text-[#1c1a17]">{r.vendor_firm || "—"}</div>
                        <div className="text-[10px] text-[#6b655b]">{r.vendor_email || "—"}</div>
                      </td>
                      <td className="py-2 pr-3">{r.type}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                            r.gateway === "bank_transfer"
                              ? "bg-blue-50 text-blue-800 border border-blue-200"
                              : "bg-neutral-100 text-neutral-700 border border-neutral-200"
                          }`}
                        >
                          {r.gateway === "bank_transfer" ? "Bank" : "Online"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold">
                        {fmtAmount(r.amount, r.currency || "INR")}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[r.status] || "bg-neutral-100 text-neutral-700 border-neutral-200"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-mono text-[10px] text-[#6b655b]">
                        {r.tracking_id || r.utr || "—"}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.completed_at || r.created_at)}</td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {r.status === "PENDING_VERIFICATION" && canManage && (
                            <>
                              {r.receipt_url && (
                                <a
                                  href={r.receipt_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`adm-payment-receipt-file-${r.order_id}`}
                                  className="inline-flex items-center gap-1 bg-white border border-[#e5e0d8] hover:border-[#234333] text-[#234333] px-2 py-1 rounded-sm text-[10px] font-bold uppercase"
                                  title="View vendor's uploaded transfer receipt"
                                >
                                  <FileDown size={11} /> View
                                </a>
                              )}
                              <button
                                type="button"
                                disabled={busyOrder === r.order_id}
                                onClick={() => verifyOffline(r)}
                                data-testid={`adm-payment-verify-${r.order_id}`}
                                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-2 py-1 rounded-sm text-[10px] font-bold uppercase"
                              >
                                {busyOrder === r.order_id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                                Verify
                              </button>
                              <button
                                type="button"
                                disabled={busyOrder === r.order_id}
                                onClick={() => { setRejectFor({ order_id: r.order_id, vendor_firm: r.vendor_firm }); setRejectReason(""); }}
                                data-testid={`adm-payment-reject-${r.order_id}`}
                                className="inline-flex items-center gap-1 bg-white border border-red-300 hover:bg-red-50 text-red-700 px-2 py-1 rounded-sm text-[10px] font-bold uppercase"
                              >
                                <XCircle size={11} /> Reject
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            disabled={r.status !== "SUCCESS" || busyOrder === r.order_id}
                            onClick={() => downloadReceipt(r.order_id)}
                            data-testid={`adm-payment-receipt-${r.order_id}`}
                            className="inline-flex items-center gap-1 bg-white border border-[#e5e0d8] hover:border-[#234333] disabled:opacity-40 text-[#234333] px-2 py-1 rounded-sm text-[10px] font-bold uppercase"
                          >
                            {busyOrder === r.order_id ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
                            Receipt
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              disabled={r.status !== "SUCCESS" || busyOrder === r.order_id || !r.vendor_email}
                              onClick={() => resendReceipt(r.order_id)}
                              data-testid={`adm-payment-resend-${r.order_id}`}
                              className="inline-flex items-center gap-1 bg-white border border-[#e5e0d8] hover:border-[#234333] disabled:opacity-40 text-[#234333] px-2 py-1 rounded-sm text-[10px] font-bold uppercase"
                              title={r.vendor_email ? "Resend receipt PDF to vendor" : "Vendor has no email on file"}
                            >
                              <Mail size={11} /> Resend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {rejectFor && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setRejectFor(null)}
          data-testid="adm-payment-reject-modal"
        >
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-700">
              <XCircle size={18} />
              <h3 className="font-serif text-xl">Reject payment submission</h3>
            </div>
            <p className="mt-2 text-sm text-[#3E3E3E]">
              Rejecting <b>{rejectFor.vendor_firm || "vendor"}</b>&apos;s submission{" "}
              <span className="font-mono text-xs">{rejectFor.order_id}</span>. The vendor will be able to submit again.
            </p>
            <label className="block mt-4 text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold">Reason *</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={500}
              autoFocus
              data-testid="adm-payment-reject-reason"
              placeholder="e.g. Credit not visible in bank statement / UTR mismatch"
              className="mt-1 w-full border border-[#e5e0d8] rounded-sm px-3 py-2 text-sm focus:border-[#234333] focus:ring-1 focus:ring-[#234333] outline-none"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectFor(null)}
                className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[#3E3E3E] hover:bg-[#f5f1e8] rounded-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={rejectOffline}
                disabled={!rejectReason.trim() || busyOrder === rejectFor.order_id}
                data-testid="adm-payment-reject-confirm"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-sm"
              >
                {busyOrder === rejectFor.order_id ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, accent = "text-[#1c1a17]", testid }) {
  return (
    <div className="bg-[#faf8f2] border border-[#e9e2d2] rounded-sm px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6b655b] font-semibold">{label}</div>
      <div className={`font-serif text-xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
